import React, { useState, useMemo, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, CheckCircle, Loader2, Copy, AlertCircle, Clock, ArrowLeft, Youtube, Twitter, Send, Video, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { WalletConnectSection } from '../components/WalletConnectButton';
import { MyQRAccess } from '../components/MyQRAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// --- Durable scout referral storage ---
const REFERRAL_STORAGE_KEY = 'tipme_scout_referral';
const PREFILL_STORAGE_KEY = 'tipme_scout_prefill';
const REFERRAL_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function saveReferral(wallet) {
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({
      wallet: wallet.toLowerCase(),
      capturedAt: Date.now(),
    }));
    console.log('[SCOUT-REF] Saved referral to localStorage:', wallet);
  } catch (e) {
    console.warn('[SCOUT-REF] Failed to save referral:', e);
  }
}

function loadReferral() {
  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.capturedAt > REFERRAL_TTL_MS) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      console.log('[SCOUT-REF] Expired referral cleared');
      return null;
    }
    console.log('[SCOUT-REF] Loaded referral from localStorage:', data.wallet);
    return data.wallet;
  } catch (e) {
    console.warn('[SCOUT-REF] Failed to load referral:', e);
    return null;
  }
}

function clearReferral() {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    console.log('[SCOUT-REF] Cleared referral from localStorage');
  } catch (e) { /* noop */ }
}

// --- Durable prefill storage (creator handle + platform) ---
const VALID_PLATFORMS = { twitter: 'twitter', x: 'twitter', youtube: 'youtube', yt: 'youtube', tiktok: 'tiktok', tt: 'tiktok' };

function savePrefill(platform, creator) {
  try {
    localStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify({
      platform, creator, capturedAt: Date.now(),
    }));
    console.log('[SCOUT-PREFILL] Saved prefill to localStorage:', { platform, creator });
  } catch (e) {
    console.warn('[SCOUT-PREFILL] Failed to save prefill:', e);
  }
}

function loadPrefill() {
  try {
    const raw = localStorage.getItem(PREFILL_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.capturedAt > REFERRAL_TTL_MS) {
      localStorage.removeItem(PREFILL_STORAGE_KEY);
      console.log('[SCOUT-PREFILL] Expired prefill cleared');
      return null;
    }
    console.log('[SCOUT-PREFILL] Loaded prefill from localStorage:', data);
    return data;
  } catch (e) {
    console.warn('[SCOUT-PREFILL] Failed to load prefill:', e);
    return null;
  }
}

function clearPrefill() {
  try {
    localStorage.removeItem(PREFILL_STORAGE_KEY);
    console.log('[SCOUT-PREFILL] Cleared prefill from localStorage');
  } catch (e) { /* noop */ }
}

export const VerifyPage = () => {
  const { address } = useAccount();
  const [searchParams] = useSearchParams();
  
  // --- Durable scout referral capture ---
  // 1. Check URL for ?ref= (highest priority — fresh link click)
  // 2. Fall back to localStorage (survives wallet connect deep links, page reloads)
  // 3. Persist to localStorage immediately on capture
  const referralFromUrl = useMemo(() => {
    const ref = searchParams.get('ref');
    if (ref && /^0x[a-fA-F0-9]{40}$/.test(ref)) return ref.toLowerCase();
    return null;
  }, [searchParams]);
  
  const [referralWallet, setReferralWallet] = useState(() => {
    // On mount: URL param takes priority, then storage
    if (referralFromUrl) {
      saveReferral(referralFromUrl);
      return referralFromUrl;
    }
    return loadReferral();
  });
  
  // If URL changes to include a new ?ref=, update state + storage
  useEffect(() => {
    if (referralFromUrl) {
      setReferralWallet(referralFromUrl);
      saveReferral(referralFromUrl);
    }
  }, [referralFromUrl]);
  
  // --- Durable platform + creator capture ---
  // Same pattern as referral: URL > localStorage > default
  const platformFromUrl = useMemo(() => {
    const raw = searchParams.get('platform');
    if (!raw) return null;
    return VALID_PLATFORMS[raw.toLowerCase().trim()] || null;
  }, [searchParams]);
  
  const creatorFromUrl = useMemo(() => {
    const raw = searchParams.get('creator');
    if (!raw) return null;
    return raw.replace(/^@/, '').trim() || null;
  }, [searchParams]);
  
  // --- Form state: URL > localStorage > default (no side effects during render) ---
  const [wasPreFilled] = useState(() => !!(platformFromUrl || creatorFromUrl || loadPrefill()));

  const [platform, setPlatform] = useState(() => {
    if (platformFromUrl) return platformFromUrl;
    const stored = loadPrefill();
    return stored?.platform || 'twitter';
  });

  const [socialHandle, setSocialHandle] = useState(() => {
    const p = platformFromUrl || loadPrefill()?.platform || 'twitter';
    const c = creatorFromUrl || loadPrefill()?.creator || '';
    return p === 'twitter' ? c : '';
  });

  const [youtubeUrl, setYoutubeUrl] = useState(() => {
    const p = platformFromUrl || loadPrefill()?.platform || 'twitter';
    const c = creatorFromUrl || loadPrefill()?.creator || '';
    return p === 'youtube' ? c : '';
  });

  const [tiktokUrl, setTiktokUrl] = useState(() => {
    const p = platformFromUrl || loadPrefill()?.platform || 'twitter';
    const c = creatorFromUrl || loadPrefill()?.creator || '';
    return p === 'tiktok' ? c : '';
  });

  // Persist to localStorage + sync form state when URL params are present
  useEffect(() => {
    if (platformFromUrl || creatorFromUrl) {
      const p = platformFromUrl || 'twitter';
      const c = creatorFromUrl || '';
      savePrefill(p, c);
      setPlatform(p);
      if (p === 'twitter') setSocialHandle(c);
      else if (p === 'youtube') setYoutubeUrl(c);
      else if (p === 'tiktok') setTiktokUrl(c);
      console.log('[SCOUT-PREFILL] Persisted + synced from URL:', { platform: p, creator: c });
    }
  }, [platformFromUrl, creatorFromUrl]);
  const [tiktokVideoUrl, setTiktokVideoUrl] = useState('');
  const [bscAddress, setBscAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  const [status, setStatus] = useState(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [submittedForReview, setSubmittedForReview] = useState(false);
  const [forceShowForm, setForceShowForm] = useState(false);  // Override verified redirect
  // Optional email for CRM (never shared publicly)
  const [contactEmail, setContactEmail] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [discoverable, setDiscoverable] = useState(true);
  
  // Pre-fill BSC address from connected wallet
  React.useEffect(() => {
    if (address && !bscAddress) {
      setBscAddress(address);
    }
  }, [address, bscAddress]);
  
  // Check status on mount
  const checkStatus = React.useCallback(async (walletToCheck) => {
    if (!walletToCheck) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/verification/status/${walletToCheck}`);
      setStatus(response.data);
      setHasCheckedStatus(true);
      
      // Late-referral fire-and-forget: if a referral is present (URL/localStorage)
      // and the status shows a pending proof with no scout referrals, push it now.
      const lateRef = referralWallet || loadReferral();
      const hasScoutReferrals = response.data?.scoutReferrals?.length > 0;
      if (lateRef && response.data?.submittedForReview && !response.data?.verified && !hasScoutReferrals) {
        console.log('[SCOUT-REF] Late-referral attach on status check:', lateRef);
        // Determine platform from proofs or URL
        const proofPlatform = response.data?.proofs?.[0]?.platform || platform;
        axios.post(`${BACKEND_URL}/api/verification/submit-for-review`, {
          wallet: walletToCheck,
          platform: proofPlatform,
          referralWallet: lateRef,
        }).then(() => {
          console.log('[SCOUT-REF] Late-referral attached successfully');
        }).catch((err) => {
          console.warn('[SCOUT-REF] Late-referral attach failed:', err?.response?.data || err.message);
        });
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setHasCheckedStatus(true);
    }
  }, [referralWallet, platform]);
  
  React.useEffect(() => {
    if (address && !hasCheckedStatus) {
      checkStatus(address);
    }
  }, [address, hasCheckedStatus, checkStatus]);
  
  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform);
    setSocialHandle('');
    setYoutubeUrl('');
    setTiktokUrl('');
    setTiktokVideoUrl('');
    setVerificationCode('');
    setVerificationData(null);
    setSubmittedForReview(false);
    setContactEmail('');
    setEmailOptIn(false);
    setDiscoverable(true);
  };
  
  const handleGoBack = () => {
    setVerificationCode('');
    setVerificationData(null);
    setStatus(null);
    setHasCheckedStatus(false);
    setSocialHandle('');
    setYoutubeUrl('');
    setTiktokUrl('');
    setTiktokVideoUrl('');
    setSubmittedForReview(false);
    setForceShowForm(true);  // Force show form even if verified
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bscAddress.trim()) {
      toast.error('Please enter your BSC address');
      return;
    }
    
    if (!/^0x[a-fA-F0-9]{40}$/.test(bscAddress.trim())) {
      toast.error('Invalid BSC address format');
      return;
    }
    
    if (platform === 'twitter' && !socialHandle.trim()) {
      toast.error('Please enter your X handle');
      return;
    }
    
    if (platform === 'youtube' && !youtubeUrl.trim()) {
      toast.error('Please enter your YouTube channel or @handle');
      return;
    }
    
    if (platform === 'tiktok' && !tiktokUrl.trim()) {
      toast.error('Please enter your TikTok username or profile URL');
      return;
    }
    
    // Resolve referral BEFORE try — catch handlers need this for late-referral rescue.
    const storageRef = loadReferral();
    const effectiveReferralWallet = referralWallet || storageRef || null;
    
    try {
      setLoading(true);
      
      if (!referralWallet && storageRef) {
        console.error('[SCOUT-REF] BUG CAUGHT: React state lost referralWallet but localStorage has it!', {
          stateValue: referralWallet,
          storageValue: storageRef,
        });
        // Also heal the React state for any subsequent renders
        setReferralWallet(storageRef);
      }
      
      console.log('[VERIFY SUBMIT PAYLOAD]', {
        effectiveReferralWallet,
        referralWalletFromState: referralWallet,
        referralWalletFromStorage: storageRef,
        referralFromUrl: searchParams.get('ref'),
        platform,
        handle: platform === 'twitter' ? socialHandle : platform === 'youtube' ? youtubeUrl : tiktokUrl,
        wallet: bscAddress.trim(),
      });
      
      let response;
      
      if (platform === 'twitter') {
        response = await axios.post(`${BACKEND_URL}/api/verification/request`, {
          wallet: bscAddress.trim(),
          socialHandle: socialHandle.trim(),
          platform: 'twitter',
          contactEmail: contactEmail.trim() || null,
          emailOptIn: emailOptIn,
          referralWallet: effectiveReferralWallet || undefined,
          discoverable: discoverable,
        });
      } else if (platform === 'youtube') {
        response = await axios.post(`${BACKEND_URL}/api/verification/youtube/request`, {
          wallet: bscAddress.trim(),
          channelUrl: (() => { const v = youtubeUrl.trim(); return (v && !v.startsWith('@') && !v.includes('youtube.com') && !v.includes('http')) ? `@${v}` : v; })(),
          contactEmail: contactEmail.trim() || null,
          emailOptIn: emailOptIn,
          referralWallet: effectiveReferralWallet || undefined,
          discoverable: discoverable,
        });
        response.data.verificationCode = response.data.verification_code || response.data.verificationCode;
      } else if (platform === 'tiktok') {
        response = await axios.post(`${BACKEND_URL}/api/verification/tiktok/request`, {
          wallet: bscAddress.trim(),
          tiktokUrl: tiktokUrl.trim(),
          contactInfo: contactEmail.trim() || null,
          referralWallet: effectiveReferralWallet || undefined,
          discoverable: discoverable,
        });
      }
      
      // Check if already verified
      if (response.data.alreadyVerified) {
        toast.success(`This ${platform === 'twitter' ? 'X' : platform === 'youtube' ? 'YouTube' : 'TikTok'} account is already verified with this wallet. No action needed!`, {
          duration: 5000
        });
        setSubmittedForReview(true);
        return;
      }
      
      // Show replacement warning if applicable
      if (response.data.isReplacement) {
        toast.warning(response.data.warning || 'You are changing the wallet for this account. The previous wallet will be replaced after approval.');
      }
      
      setVerificationCode(response.data.verificationCode);
      setVerificationData(response.data);
      toast.success('Verification code generated!');
      
      // Referral was persisted to backend — clear local storage
      if (effectiveReferralWallet) {
        console.log('[SCOUT-REF] Referral sent to backend successfully:', effectiveReferralWallet, '— clearing localStorage');
        clearReferral();
      }
      clearPrefill();
      
    } catch (error) {
      console.error('Verification error:', error);
      const errorDetail = error.response?.data?.detail || '';
      const statusCode = error.response?.status;
      
      // Handle rate limiting (429)
      if (statusCode === 429) {
        toast.error(errorDetail || 'Too many requests. Please wait a few minutes before trying again.');
        // Late-referral rescue: even though the main request was rate-limited,
        // push the referral via submit-for-review so it attaches to the existing proof.
        if (effectiveReferralWallet) {
          console.log('[SCOUT-REF] Rate-limited — attempting late-referral merge via submit-for-review');
          try {
            await axios.post(`${BACKEND_URL}/api/verification/submit-for-review`, {
              wallet: bscAddress.trim(),
              platform,
              referralWallet: effectiveReferralWallet,
            });
            console.log('[SCOUT-REF] Late-referral merged after 429');
          } catch (mergeErr) {
            console.warn('[SCOUT-REF] Late-referral merge failed:', mergeErr?.response?.data || mergeErr.message);
          }
        }
        return;
      }
      
      // Handle PENDING_SAME_WALLET — proof exists, merge referral
      if (errorDetail.includes('PENDING_SAME_WALLET') || errorDetail.includes('PENDING_ALREADY_EXISTS')) {
        if (effectiveReferralWallet) {
          console.log('[SCOUT-REF] Existing pending proof — attempting late-referral merge');
          try {
            await axios.post(`${BACKEND_URL}/api/verification/submit-for-review`, {
              wallet: bscAddress.trim(),
              platform,
              referralWallet: effectiveReferralWallet,
            });
            console.log('[SCOUT-REF] Late-referral merged on existing pending');
          } catch (mergeErr) {
            console.warn('[SCOUT-REF] Late-referral merge failed:', mergeErr?.response?.data || mergeErr.message);
          }
        }
      }
      
      // Handle specific error cases with user-friendly messages
      if (errorDetail.includes('PENDING_SAME_WALLET')) {
        toast.error('You already have a pending verification for this channel. Please wait for it to be reviewed.');
      } else if (errorDetail.includes('PENDING_DIFFERENT_WALLET')) {
        toast.error('This channel has a pending verification from another wallet. Please wait for that to be reviewed first.');
      } else if (errorDetail.includes('PENDING_ALREADY_EXISTS')) {
        toast.error('This YouTube channel already has a verification pending. Please wait for it to be reviewed before submitting again.');
      } else if (errorDetail.includes('ALREADY_VERIFIED_SAME_WALLET')) {
        toast.info('This account is already verified with this wallet. No action needed!');
      } else if (errorDetail.includes('ALREADY_VERIFIED') || errorDetail.toLowerCase().includes('already verified')) {
        // This shouldn't normally happen since backend allows replacement
        // But if it does, explain the situation
        toast.info('This account is already verified with another wallet. Submit with your new wallet to request a transfer (requires admin approval).');
      } else {
        toast.error(errorDetail || 'Failed to request verification');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    try {
      setSubmitting(true);
      
      // Resolve referral at submit time (URL > localStorage > state)
      const lateRef = loadReferral();
      const resolvedReferral = referralWallet || lateRef || null;
      console.log('[VERIFY SUBMIT-FOR-REVIEW PAYLOAD FULL]', {
        wallet: bscAddress.trim(),
        platform,
        referralWallet: resolvedReferral,
        source: referralWallet ? 'state' : lateRef ? 'localStorage' : 'none',
      });
      
      // For TikTok, we need to submit the video URL first
      if (platform === 'tiktok') {
        if (!tiktokVideoUrl.trim()) {
          toast.error('Please enter the TikTok video URL where you posted the code');
          setSubmitting(false);
          return;
        }
        
        await axios.post(`${BACKEND_URL}/api/verification/tiktok/submit-video`, {
          wallet: bscAddress.trim(),
          videoUrl: tiktokVideoUrl.trim()
        });
        
        setSubmittedForReview(true);
        toast.success('Video URL submitted! An admin will review your verification.');
        return;
      }
      
      // Submit for review - works for both X and YouTube
      // Always merge referral: covers late-arrival (scout link clicked after initial submission)
      const payload = {
        wallet: bscAddress.trim(),
        platform: platform,
      };
      if (resolvedReferral) {
        payload.referralWallet = resolvedReferral;
      }
      await axios.post(`${BACKEND_URL}/api/verification/submit-for-review`, payload);
      
      setSubmittedForReview(true);
      toast.success('Request submitted for review!');
      
    } catch (error) {
      console.error('Submit error:', error);
      // Even if endpoint fails, show success (request is already pending from code generation)
      setSubmittedForReview(true);
      toast.success('Request submitted for review!');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(verificationCode)
        .then(() => toast.success('Verification code copied!'))
        .catch((err) => {
          console.error('Clipboard API failed:', err);
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  };
  
  const fallbackCopy = () => {
    // Fallback for older browsers or when clipboard API fails
    try {
      const textArea = document.createElement('textarea');
      textArea.value = verificationCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Verification code copied!');
      } else {
        toast.error('Failed to copy - please copy manually');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Failed to copy - please copy manually');
    }
  };

  // Verified state - but allow user to add more accounts
  if (status?.verified && hasCheckedStatus && !verificationCode && !forceShowForm) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-6">
        <div className="max-w-md mx-auto pt-10">
          <Card className="p-8 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center">
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-[#1a1a1a]">Verified!</h2>
            <p className="text-[#666] mb-6">Your wallet is verified and eligible for TRUE Points rewards.</p>
            <div className="bg-[#d4cfc4] rounded-xl p-4 mb-6">
              <div className="text-xs text-[#888] mb-1">Wallet Address</div>
              <div className="font-mono text-sm text-[#333] break-all">{address || bscAddress}</div>
            </div>
            <Button 
              onClick={handleGoBack} 
              className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
            >
              Add Another Social Account
            </Button>
            <p className="text-xs text-[#888] mt-3">
              Link additional social accounts or change wallet for existing ones
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // Pending review state (from status check)
  if (status?.submittedForReview && !verificationCode && hasCheckedStatus && !forceShowForm) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-6">
        <div className="max-w-md mx-auto pt-10">
          <Card className="p-8 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center">
            <div className="w-16 h-16 bg-[#f0b90b] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-[#1a1a1a]">Pending Review</h2>
            <p className="text-[#666] mb-6">Your verification request is being reviewed. This usually takes less than 24 hours.</p>
            <Button 
              onClick={handleGoBack} 
              className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
            >
              Add Another Social Account
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Submitted for review state (after clicking submit button)
  if (submittedForReview) {
    return (
      <div className="min-h-screen bg-[#c8c3bb] p-6">
        <div className="max-w-md mx-auto pt-10">
          <Card className="p-8 bg-[#e8e3db] border-0 shadow-lg rounded-2xl text-center" data-testid="verification-success">
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-[#1a1a1a]">✅ Request Submitted</h2>
            <p className="text-[#666] mb-4">Your verification request is now in the review queue.</p>
            
            <div className="bg-[#d4cfc4] rounded-xl p-4 mb-4">
              <div className="flex items-center justify-center gap-2 text-[#1a1a1a]">
                <Clock className="w-4 h-4" />
                <span className="font-semibold">Status: Pending</span>
              </div>
            </div>
            
            <Alert className="mb-6 border-[#d4cfc4] bg-[#d4cfc4]/50 text-left">
              <AlertCircle className="h-4 w-4 text-[#666]" />
              <AlertDescription className="text-[#666] text-sm">
                <strong>Important:</strong> Keep the verification code visible on your {platform === 'twitter' ? 'X profile' : 'YouTube channel'} until you receive approval.
                <br /><br />
                Manual review typically takes <strong>less than 24 hours</strong>.
              </AlertDescription>
            </Alert>
            
            <button 
              onClick={handleGoBack} 
              className="text-sm text-[#666] hover:text-[#1a1a1a] underline underline-offset-2"
              data-testid="start-another-verification"
            >
              Start another verification
            </button>
          </Card>
        </div>
      </div>
    );
  }

  // Platform-specific instructions
  const renderInstructions = () => {
    if (platform === 'twitter') {
      return (
        <>
          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">2</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Post on X (Twitter)</span>
            </div>
            <p className="text-[#666] text-sm ml-8">
              Post a <strong className="text-[#1a1a1a]">public tweet</strong> containing your verification code.
            </p>
          </div>

          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">3</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Submit for Review</span>
            </div>
            <p className="text-[#666] text-sm ml-8">
              Click the button below to add your request to the review queue.
            </p>
          </div>
        </>
      );
    } else if (platform === 'youtube') {
      return (
        <>
          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">2</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Add to your YouTube channel</span>
            </div>
            <div className="text-[#666] text-sm ml-8 space-y-2">
              <p>Add this code to <strong className="text-[#1a1a1a]">ONE</strong> of:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Channel About section</strong> (recommended)</li>
                <li><strong>Any video description</strong></li>
              </ul>
            </div>
          </div>

          <Alert className="border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700 text-sm">
              <strong>Comments are NOT accepted</strong> — only About section or video descriptions.
            </AlertDescription>
          </Alert>

          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">3</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Submit for Review</span>
            </div>
            <p className="text-[#666] text-sm ml-8">
              Click the button below to add your request to the review queue.
            </p>
          </div>
        </>
      );
    } else if (platform === 'tiktok') {
      return (
        <>
          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">2</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Post a TikTok Video</span>
            </div>
            <div className="text-[#666] text-sm ml-8 space-y-2">
              <p>Create a video and add the code to the <strong className="text-[#1a1a1a]">video caption</strong>.</p>
              <p className="text-xs text-[#888]">Short videos are fine — the content doesn't matter, only the caption.</p>
            </div>
          </div>

          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">3</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Paste Your Video URL</span>
            </div>
            <div className="ml-8">
              <p className="text-[#666] text-sm mb-2">After posting, paste the link to your video here:</p>
              <Input
                placeholder="https://tiktok.com/@username/video/123456..."
                value={tiktokVideoUrl}
                onChange={(e) => setTiktokVideoUrl(e.target.value)}
                className="bg-white border-[#d4cfc4] text-[#1a1a1a] placeholder:text-[#999]"
                data-testid="tiktok-video-url-input"
              />
            </div>
          </div>

          <div className="bg-[#d4cfc4] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs">4</span>
              </div>
              <span className="font-semibold text-[#1a1a1a]">Submit for Review</span>
            </div>
            <p className="text-[#666] text-sm ml-8">
              Click the button below to send your video link for admin review.
            </p>
          </div>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--tm-bg-primary)' }}>
      <div className="max-w-md mx-auto pt-10">
        {/* Wallet Connect */}
        <WalletConnectSection 
          message="Connect your wallet to auto-fill your BSC address" 
          className="mb-8"
        />
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--tm-yellow)' }}>
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 style={{ fontFamily: "'Nunito', sans-serif", fontSize: '2rem', fontWeight: 300, color: 'var(--tm-text-primary)', marginBottom: '0.5rem' }}>Get Verified</h1>
          <p style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 400, color: 'var(--tm-text-secondary)' }}>Link your social account to become eligible for TRUE Points rewards</p>
        </div>

        {/* Rejection Alert */}
        {status?.rejectionReason && (
          <Alert className="mb-6 border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700">
              Your previous request was rejected: {status.rejectionReason}
            </AlertDescription>
          </Alert>
        )}

        {/* Scout referral indicator */}
        {referralWallet && (
          <div className="mb-4 px-4 py-3 bg-[#f0b90b]/10 border border-[#f0b90b]/25 rounded-xl" data-testid="scout-referral-banner">
            <div className="flex items-center gap-2">
              <Send className="w-3.5 h-3.5 text-[#b5960a] flex-shrink-0" />
              <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '0.82rem', color: '#8b7a2e' }}>
                Referred by scout
              </span>
            </div>
            <div className="mt-1 font-mono text-xs text-[#8b7a2e]/70 break-all pl-5">
              {referralWallet}
            </div>
          </div>
        )}

        {/* Scout Affiliation CTA */}
        <div className="mb-4 px-4 py-3 border border-[#d4cfc4] rounded-xl bg-[#e8e3db]/60" data-testid="scout-cta-block">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#1a1a1a]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                Invited by a scout?
              </p>
              <p className="text-xs text-[#888] mt-0.5 leading-relaxed">
                Learn how TipMe affiliation works for creators and scouts.
              </p>
            </div>
            <a
              href="https://tipme.space/scout"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#b5960a] hover:text-[#8b7a2e] transition-colors whitespace-nowrap"
              data-testid="scout-cta-link"
            >
              How it works
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <Card className="p-6 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          {!verificationCode ? (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="text-[#1a1a1a] font-semibold">Platform</Label>
                {wasPreFilled && (
                  <span className="ml-2 text-[10px] text-[#b5960a]" data-testid="platform-prefill-hint">Pre-filled from referral link</span>
                )}
                <Select value={platform} onValueChange={handlePlatformChange}>
                  <SelectTrigger className="mt-2 bg-[#d4cfc4] border-0 text-[#1a1a1a]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitter">
                      <div className="flex items-center gap-2">
                        <Twitter className="w-4 h-4" />
                        X (Twitter)
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube">
                      <div className="flex items-center gap-2">
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </div>
                    </SelectItem>
                    <SelectItem value="tiktok">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        TikTok
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {platform === 'twitter' ? (
                <div>
                  <Label className="text-[#1a1a1a] font-semibold">Your X Handle</Label>
                  {wasPreFilled && socialHandle && (
                    <span className="ml-2 text-[10px] text-[#b5960a]" data-testid="handle-prefill-hint">Pre-filled from referral link</span>
                  )}
                  <Input
                    placeholder="@username"
                    value={socialHandle}
                    onChange={(e) => setSocialHandle(e.target.value)}
                    className="mt-2 bg-[#d4cfc4] border-0 text-[#1a1a1a] placeholder:text-[#999]"
                  />
                </div>
              ) : platform === 'youtube' ? (
                <div>
                  <Label className="text-[#1a1a1a] font-semibold">Your YouTube Channel</Label>
                  <Input
                    placeholder="@MrBeast or youtube.com/@MrBeast"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    className="mt-2 bg-[#d4cfc4] border-0 text-[#1a1a1a] placeholder:text-[#999]"
                  />
                  <p className="text-xs text-[#888] mt-2">
                    Paste your channel URL or @handle. We'll take care of the rest.
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-[#1a1a1a] font-semibold">Your TikTok Username</Label>
                  <Input
                    placeholder="@username or tiktok.com/@username"
                    value={tiktokUrl}
                    onChange={(e) => setTiktokUrl(e.target.value)}
                    className="mt-2 bg-[#d4cfc4] border-0 text-[#1a1a1a] placeholder:text-[#999]"
                    data-testid="tiktok-url-input"
                  />
                  <p className="text-xs text-[#888] mt-2">
                    Enter your TikTok username or profile URL.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-[#1a1a1a] font-semibold">BSC Wallet Address</Label>
                <Input
                  placeholder="0x..."
                  value={bscAddress}
                  onChange={(e) => setBscAddress(e.target.value)}
                  className="mt-2 bg-[#d4cfc4] border-0 text-[#1a1a1a] font-mono text-sm placeholder:text-[#999]"
                />
                <p className="text-xs text-[#888] mt-1">This is the wallet where you'll receive TIP rewards</p>
              </div>

              {/* Optional Email for Updates */}
              <div className="border border-[#d4cfc4] rounded-xl p-4 bg-[#d4cfc4]/30">
                <Label className="text-[#1a1a1a] font-semibold">Contact Email (Optional)</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="mt-2 bg-white border-0 text-[#1a1a1a] placeholder:text-[#999]"
                  data-testid="contact-email-input"
                />
                <div className="flex items-start gap-2 mt-3">
                  <input
                    type="checkbox"
                    id="emailOptIn"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    className="mt-1 rounded border-[#d4cfc4]"
                    data-testid="email-opt-in-checkbox"
                  />
                  <label htmlFor="emailOptIn" className="text-xs text-[#666] leading-relaxed">
                    I'd like to receive important updates about my verification status and TipMe features. 
                    <span className="block text-[#888] mt-1">
                      Your email is <strong>never shared publicly</strong> or with third parties.
                    </span>
                  </label>
                </div>
              </div>

              {/* Discover Visibility Toggle */}
              <div className="flex items-start gap-2" data-testid="discover-toggle-section">
                <input
                  type="checkbox"
                  id="discoverable"
                  checked={discoverable}
                  onChange={(e) => setDiscoverable(e.target.checked)}
                  className="mt-1 rounded border-[#d4cfc4]"
                  data-testid="discoverable-checkbox"
                />
                <label htmlFor="discoverable" className="text-xs text-[#666] leading-relaxed">
                  <span className="font-semibold text-[#1a1a1a]">Show my profile on TipMe</span>
                  <span className="block text-[#888] mt-0.5">
                    Allow your profile to appear on TipMe so others can find and tip you.
                  </span>
                </label>
              </div>

              <Alert className="border-[#d4cfc4] bg-[#d4cfc4]/50">
                <Clock className="h-4 w-4 text-[#666]" />
                <AlertDescription className="text-[#666] text-sm">
                  Verification is <strong>manually reviewed</strong>. Approval typically takes <strong>up to 24 hours</strong>.
                </AlertDescription>
              </Alert>

              <Button type="submit" disabled={loading} className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Get Verification Code'}
              </Button>
            </form>
          ) : (
            /* Code Generated - Show Instructions */
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-[#1a1a1a]">Code Generated!</h3>
                <p className="text-[#666] text-sm mt-1">Follow the steps below</p>
              </div>

              {/* Step 1 - Copy Code */}
              <div className="bg-[#f0b90b]/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-[#f0b90b] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">1</span>
                  </div>
                  <span className="font-semibold text-[#1a1a1a]">Copy your unique code</span>
                </div>
                <div className="flex gap-2">
                  <Input value={verificationCode} readOnly className="bg-white border-[#f0b90b] font-mono font-bold text-[#f0b90b]" onClick={(e) => e.target.select()} />
                  <Button onClick={copyCode} variant="outline" className="border-[#d4cfc4] hover:bg-[#d4cfc4]">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Platform-specific instructions */}
              {renderInstructions()}

              {/* Primary CTA - Submit for Review */}
              <Button 
                onClick={handleSubmitForReview} 
                disabled={submitting}
                className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold py-6 text-lg"
                data-testid="submit-for-review-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> I've added the code — Submit for review</>
                )}
              </Button>
              
              <p className="text-xs text-center text-[#888]">
                This will send your request to the admin review queue.<br />
                Keep the code visible until approved.
              </p>

              {/* Secondary action - small link, not primary button */}
              <button 
                onClick={handleGoBack} 
                className="text-sm text-[#666] hover:text-[#1a1a1a] underline underline-offset-2 w-full text-center"
                data-testid="start-another-verification-link"
              >
                Start another verification
              </button>
            </div>
          )}
        </Card>
        
        {/* My QR Code Access */}
        <div className="mt-6">
          <MyQRAccess variant="compact" showWhenNoQR={true} />
        </div>
      </div>
    </div>
  );
};
